import { InternalOrder, KeyPerformanceIndicator, SecondaryKeyPerformanceIndicatorPost } from '@abovo/shared-models';
import { useSnackbar } from '@abovo/shared-services';
import { MAT_DIALOG_TYPES } from '@abovo/shared-ui-components';
import { CategoryGroupKey, isDefined } from '@abovo/shared-utils';
import { primaryKpi, SecondaryKeyPerformanceIndicatorStore } from '@abovo/webvis-data-access';
import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SecondaryKpiEntry, SecondaryKpiOrder } from '../components/secondary-kpis/secondary-kpis.component';
import { SecondaryKpiDialogComponent } from '../dialogs/secondary-kpi-dialog/secondary-kpi-dialog.component';

/**
 * The slice of a canonical order needed to open the secondary KPI dialog: the display fields of
 * {@link SecondaryKpiOrder} plus the KPIs. A full {@link InternalOrder} fits as-is — the primary
 * KPI then comes from its `kpi[0]` and `secondary_kpis` passes through untouched. Callers editing
 * pending state (the intermediate report grid) override `primary` and `secondary_kpis` with their
 * form values instead.
 */
export interface SecondaryKpiDialogOrder extends SecondaryKpiOrder, Pick<InternalOrder, 'kpi'> {
   /** Overrides the order's own primary KPI values, e.g. with pending form state. */
   primary?: {
      kpi?: KeyPerformanceIndicator;
      kpi_value?: number;
      purchase_rate_value?: number;
   };

   /** Existing secondary KPIs — the record ids target update/delete on persist. */
   secondary_kpis?: Array<SecondaryKpiEntry>;
}

export interface UseSecondaryKpiDialogOptions {
   readonly?: boolean;
   currentDiscipline?: CategoryGroupKey;

   /** When true (default), the hook persists create/update/delete calls to the backend. Set to false for DVA wizard where changes are accumulated locally until the whole order is saved. */
   persist?: boolean;

   /** When true, hide the order details header and the result section with value inputs (used when values are edited elsewhere, e.g. intermediate report grid) */
   selectionOnly?: boolean;
}

export const useSecondaryKpiDialog = () => {
   const dialog = inject(MatDialog);
   // Optional because callers with `persist: false` don't need the backend store — and providing it may not be available in all contexts.
   const secondaryKpiStore = inject(SecondaryKeyPerformanceIndicatorStore, { optional: true });
   const snackbar = useSnackbar();
   const translateService = inject(TranslateService);

   return (order: SecondaryKpiDialogOrder, options: UseSecondaryKpiDialogOptions = {}): Promise<Array<SecondaryKpiEntry> | null> => {
      const { readonly = false, currentDiscipline, persist = true, selectionOnly = false } = options;

      const orderPrimary = primaryKpi(order);
      const primaryValues =
         order.primary ??
         (orderPrimary && {
            kpi: orderPrimary.kpi,
            kpi_value: orderPrimary.kpi_value?.value,
            purchase_rate_value: orderPrimary.purchase_rate_value?.value,
         });
      const primary = primaryValues?.kpi && {
         kpi: primaryValues.kpi,
         kpi_value: primaryValues.kpi_value ?? 0,
         rate: primaryValues.purchase_rate_value ?? 0,
         nett_purchase_price: order.nett_purchase_price ?? 0,
      };

      const dialogRef = dialog.open(SecondaryKpiDialogComponent, {
         ...MAT_DIALOG_TYPES.middle,
         data: {
            kpis: order.secondary_kpis ?? [],
            order,
            readonly,
            primaryKpi: primary,
            currentDiscipline,
            selectionOnly,
         },
      });

      return new Promise<Array<SecondaryKpiEntry> | null>((resolve) => {
         dialogRef.componentInstance.selectedKpisList.subscribe(async (changedKpiList) => {
            const selected = changedKpiList.filter((kpi) => !kpi.is_primary_kpi);

            if (!persist || !order.id) {
               resolve(selected);
               dialogRef.close();
               return;
            }

            if (!secondaryKpiStore) {
               throw new Error('SecondaryKeyPerformanceIndicatorStore is required when persist=true');
            }

            try {
               await persistChanges(order.id, order.secondary_kpis ?? [], selected, secondaryKpiStore);
               snackbar.show(translateService.instant('snackbar.update.success', { subject: translateService.instant('grid.kpis') }));
               resolve(selected);
               dialogRef.close();
            } catch (error) {
               snackbar.show(translateService.instant('snackbar.update.fail', { subject: translateService.instant('grid.kpis') }));
               dialogRef.componentInstance.saving.set(false);
               console.error(error);
            }
         });

         // Resolve with null when the dialog is closed without saving (cancel, backdrop click, ESC).
         // Subscription auto-completes on afterClosed — no unsubscribe needed.
         // Promise.resolve is idempotent: if save already resolved first, this call is a no-op.
         dialogRef.afterClosed().subscribe(() => resolve(null));
      });
   };
};

/** Diff the selected KPIs against the order's existing secondary KPIs and send create/update/delete to the backend. */
async function persistChanges(
   orderId: number,
   existingKpis: Array<SecondaryKpiEntry>,
   selected: Array<SecondaryKpiEntry>,
   store: InstanceType<typeof SecondaryKeyPerformanceIndicatorStore>,
): Promise<void> {
   const calls: Array<Promise<boolean>> = [];

   // Create or update: entries that are new, or whose kpi_value differs from the original
   const toCreateOrUpdate = selected.filter((kpi) => {
      const original = existingKpis.find((existing) => existing.kpi.id === kpi.kpi.id);
      return !original || original.kpi_value !== kpi.kpi_value;
   });
   if (toCreateOrUpdate.length) {
      const posts: SecondaryKeyPerformanceIndicatorPost[] = toCreateOrUpdate.map((kpi) => ({
         id: kpi.id,
         kpi: kpi.kpi.id,
         order: orderId,
         kpi_value: kpi.kpi_value ?? 0,
         kpi_investment: kpi.kpi_investment,
      }));
      calls.push(store.createUpdateMulti(posts));
   }

   // Delete: entries in the order that are no longer in the selection
   const toDelete = existingKpis.filter((existing) => !selected.some((kpi) => kpi.kpi.id === existing.kpi.id));
   if (toDelete.length) {
      calls.push(store.deleteMulti(toDelete.map((kpi) => kpi.id).filter(isDefined)));
   }

   await Promise.all(calls);
}
